import {
  mapApiMenuToMenuItemV2,
  mapMenuItemV2ToLegacyShape,
  type ApiMenuCatalogItemRaw,
  type MenuItemV2WithCategory,
} from "@/adapters/menuV2Adapter";
import { buildMenuCatalogSnapshot } from "@/domain/cart/menu-catalog.snapshot";
import {
  getLatestMenuCatalogSnapshot,
  setLatestMenuCatalogSnapshot,
} from "@/domain/cart/menu-catalog.registry";
import type { MenuCatalogSnapshot } from "@/domain/cart/cart-validity.types";
import { MenuItemInterface } from "@/models/menu-item";
import API from "@/utils/api";
import { WEBSITE_ORDER_CHANNEL } from "@/constants/orderChannel";
import { buildPublicMenuPath } from "@/services/public-storefront-paths";
import { usesHostOnlyPublicPaths } from "@/config/restaurant-id";

export type EnsureCatalogLoadedOptions = {
  force?: boolean;
  signal?: AbortSignal;
  /** PLATFORM_SLUG identity; ignored for CUSTOM_HOSTNAME host-only paths. */
  slug?: string;
};

export type EnsureCatalogLoadedResult = {
  ok: boolean;
  catalog: MenuCatalogSnapshot;
  menuItems: MenuItemInterface[];
};

/** Public menu catalog path — mode-aware. */
export function buildStorefrontMenuCatalogPath(
  slug?: string | null,
  orderChannel: number = WEBSITE_ORDER_CHANNEL
): string {
  return buildPublicMenuPath(slug, orderChannel);
}

function extractCatalogSnapshotRows(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.data)) return o.data;
  }
  return [];
}

function buildUnavailableCatalog(restaurantId: string): MenuCatalogSnapshot {
  return buildMenuCatalogSnapshot({
    items: [],
    restaurantId,
    restaurantAvailable: false,
    loaded: false,
  });
}

function mapRowsToMenuItems(rows: unknown[]): MenuItemInterface[] {
  const menuItemsV2: MenuItemV2WithCategory[] = rows.map((row) =>
    mapApiMenuToMenuItemV2(row as ApiMenuCatalogItemRaw)
  );

  return menuItemsV2
    .filter((item) => !item.is_deleted)
    .map((v2) => mapMenuItemV2ToLegacyShape(v2, { orderChannel: WEBSITE_ORDER_CHANNEL }));
}

/**
 * Fetches and registers an authoritative menu catalog snapshot for reorder/checkout.
 * Does not mutate cart. Updates the module registry on success or failure.
 */
export async function ensureCatalogLoaded(
  restaurantId: string,
  options?: EnsureCatalogLoadedOptions & { slug?: string }
): Promise<EnsureCatalogLoadedResult> {
  const scopedRestaurantId = restaurantId.trim();
  const slug = (options?.slug ?? "").trim();
  if (!scopedRestaurantId) {
    const catalog = buildUnavailableCatalog("unknown");
    setLatestMenuCatalogSnapshot(catalog);
    return { ok: false, catalog, menuItems: [] };
  }

  if (!slug && !usesHostOnlyPublicPaths()) {
    const catalog = buildUnavailableCatalog(scopedRestaurantId);
    setLatestMenuCatalogSnapshot(catalog);
    return { ok: false, catalog, menuItems: [] };
  }

  if (!options?.force) {
    const existing = getLatestMenuCatalogSnapshot();
    if (
      existing?.loaded &&
      existing.restaurantAvailable &&
      existing.restaurantId.trim() === scopedRestaurantId
    ) {
      return { ok: true, catalog: existing, menuItems: [] };
    }
  }

  try {
    const response = await API.get(buildStorefrontMenuCatalogPath(slug), {
      headers: { Accept: "application/json" },
      signal: options?.signal,
    });

    const rawRows = extractCatalogSnapshotRows(response.data);
    const list = mapRowsToMenuItems(rawRows);
    const catalog = buildMenuCatalogSnapshot({
      items: list,
      restaurantId: scopedRestaurantId,
      restaurantAvailable: true,
      loaded: true,
    });
    setLatestMenuCatalogSnapshot(catalog);
    return { ok: true, catalog, menuItems: list };
  } catch {
    const catalog = buildUnavailableCatalog(scopedRestaurantId);
    setLatestMenuCatalogSnapshot(catalog);
    return { ok: false, catalog, menuItems: [] };
  }
}
